<template>
  <div :class="fadeInClass">
    <div v-if="type">
      <question-asker v-if="showQuestions" :questions="questionsToAsk" @cancel="handleQuestionCancel"
        @complete="handleQuestionComplete" />

      <GetTextComponent v-if="showCouponModal" :isOpen="showCouponModal" :title="$t('couponCode')"
        :instructions="$t('couponCodeInstructions')" :buttonText="$t('apply')" :validate="validateCouponCode"
        @close="showCouponModal = false, setBarcodeActive(true)" @apply="handleApplyCoupon" />

      <AgeVerification
        v-if="showAgeVerification && config?.ageVerification"
        :visible="showAgeVerification"
        :config="config.ageVerification"
        @complete="onAgeVerificationComplete"
        @cancel="onAgeVerificationCancel"
      />

      <template v-if="orderType.view === 'retail'">
        <item-order @close="close" :showSpecialRequest="config.allowSpecialRequests"
          :initialModifications="selectedItem?.lineModifierSets || []" />
        <SpaceX @item-selected="seachSelected" :total="total" @barcode-scanned="handleScan" @removed="removeItem"
          @reset="reset" @checkout="handleCheckout" @applyCoupon="showCouponModalHandler"
          @removeCoupon="handleRemoveCoupon" :cart-title="$t('cartTitle')" :departments="departments" :items="items"
          :order-items="orderItems" :selected-order-type="orderType" @setBarcodeActive="setBarcodeActive"
          :order-types="orderTypes" :hasCoupons="hasCoupons" :coupon="coupon"
          :search-items="searchItems" />

      </template>
      <template v-else>
        <item-order-v2 v-if="useItemOrderV2" @close="close" :showQuantity="!hideQuantityControl"
          :showSpecialRequest="config.allowSpecialRequests" />
        <item-order v-else @close="close" :showQuantity="!hideQuantityControl"
          :showSpecialRequest="config.allowSpecialRequests" />
        <div class="top-right">
          <LanguageSelector v-if="config" :default-language="config.languageSupport.defaultLanguage"
            :enabled="config.languageSupport.enabled" :supported-languages="config.languageSupport.supportedLanguages"
            :use-modal="true" />
        </div>

        <div class="layout">
          <div class="menu-col" ref="menu">
            <span ref="locationInfo" />
            <LocationInfo :logoUrl="config.logo?.url" :logoShape="config.logo?.shape || 'circle'"
              :locationName="details.name" :fullAddress="fullAddress" />

            <PillNavigation class="tab-nav" :tabs="departmentTabs" :nav-top="navTop" :logo="config.logo?.url"
              :shape="config.logo?.shape || 'circle'" :selectedTabRef="selectedDepartmentTab"
              :pill-size="config.pillNavSize || 'normal'" @tab-selected="departmentTabSelected" ref="nav" />

            <!-- Banner Messages: moved here, above departments -->
            <div v-if="orderType?.messages && bannerMessages.length" class="banner-messages">
              <transition-group name="banner-fade" tag="div">
                <div v-for="message in bannerMessages" :key="message.id"
                     class="alert banner-alert d-flex align-items-start"
                     :class="`alert-${message.bannerType || 'info'}`">
                  <div class="banner-icon" v-if="message.bannerType === 'warning' || message.bannerType === 'danger'">
                    <i class="bi bi-exclamation-triangle-fill"></i>
                  </div>
                  <div class="banner-icon" v-else-if="message.bannerType === 'info'">
                    <i class="bi bi-info-circle-fill"></i>
                  </div>
                  <div class="banner-icon" v-else-if="message.bannerType === 'success'">
                    <i class="bi bi-check-circle-fill"></i>
                  </div>
                  <div class="banner-content flex-grow-1">
                    <h4 v-if="message.title" class="alert-heading">{{ message.title }}</h4>
                    <p class="mb-0" style="white-space: pre-line;">{{ message.text }}</p>
                  </div>
                  <button class="banner-close" @click="dismissedBanners.push(message.id)" aria-label="Close banner">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </transition-group>
            </div>

            <div class="departments">
              <!-- Fast Item Group Departments -->
              <Department v-for="groupName in fastItemGroupNamesSorted"
                :key="`fast-${groupName}`"
                :name="groupName"
                :id="`dep-fast-${groupName}`"
                :items="getFastItemsByGroup(groupName)"
                :cols="Number(config.menu_cols)"
                @item-selected="menuSelected"/>
              
              <!-- Regular departments -->
              <Department v-for="department in departments" :key="department.ref" :name="department.name"
                :id="`dep-${department.ref}`" :items="departmentItems(department)" :cols="Number(config.menu_cols)"
                :description="department.description" @item-selected="menuSelected" />
            </div>

          </div>
          <div class="order-col">
            <OrderControls :customerName="customerName" :orderCount="orderItems.length" :cart-title="$t('cartTitle')"
              :orderTypes="orderTypes" :selected="orderTypeId" @orderTypeSelected="orderTypeSelected" @reset="reset" />
            <OrderSummary :use-icons="config.icons" :items="orderItems" :emptyCartMessage="$t('emptyCartMessage')"
              :discounts="discounts" :coupon="coupon" :subtotal="subtotal" :taxTotal="taxTotal" :total="total"
              @removed="removeItem"
              @checkout="handleCheckout" @edit="handleItemEdit" @reset="reset" @applyCoupon="showCouponModalHandler"
              @removeCoupon="handleRemoveCoupon"
              :showQuantityEdit="orderType.view === 'restaurant' && !hideQuantityControl"
              :next-button-text="nextButtonText" :payment-types="orderType.allowedPaymentTypes" :hasCoupons="hasCoupons"
              :show-item-details="config.orderSummaryShowModifiers || false" :surcharges="surcharges || []" />
          </div>
        </div>
      </template>
      <BarcodeScanner @scan="handleScan" :is-active="barcodeActive" :show-debug="config?.showScanDebug || config?.enableWeightedBarcodes"
        :weighted-enabled="config?.enableWeightedBarcodes"
        aria-label="Barcode Scanner" role="application" />
      <QuantityPrompt
        :isOpen="showQuantityPrompt"
        :itemName="quantityPromptItem?.name || ''"
        :price="quantityPromptItem?.price"
        :defaultQuantity="quantityPromptDefault"
        @close="showQuantityPrompt = false; quantityPromptItem = null"
        @confirm="onQuantityPromptConfirm"
      />
    </div>
    <ErrorModal :visible="errorModalVisible" :message="errorMessage" @back="closeErrorModal" @startOver="reset"
      :allowBack="true" :allowStartOver="true" />
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import LanguageSelector from "@/components/features/LanguageSelector.vue";
import ItemOrder from "@/components/features/itemOrder/ItemOrder.vue";
import ItemOrderV2 from "@/components/features/itemOrderV2/ItemOrderV2.vue";
import BarcodeScanner from "@/components/features/BarcodeScanner.vue";
import LocationInfo from "@/components/global/LocationInfo.vue";
import PillNavigation from "@/components/global/PillNavigation.vue";
import GetTextComponent from "@/components/global/GetTextModal.vue";
import Department from "@/components/features/menu/MenuDepartment.vue";
import SpaceX from "@/components/features/menu/spaceX/MainLayout.vue";
import OrderControls from "@/components/features/order/OrderControls.vue";
import OrderSummary from "@/components/features/order/OrderSummary.vue";
import QuestionAsker from "@/components/features/QuestionAsker.vue";
import ErrorModal from "@/components/global/ErrorModal.vue";
import QuantityPrompt from "@/components/global/QuantityPrompt.vue";
import lookupList from "@/utils/lookupList";
import { formatCurrency, formatAddress } from "@/utils/helpers";
import AgeVerification from '@/components/features/AgeVerification.vue';


const preloadSpinner = new Image();
preloadSpinner.src = require('@/assets/spinner.png');

export default {
  inject: ["translator"],
  data() {
    return {
      showQuestions: false,
      questionsToAsk: [],
      rightColVisible: false,
      selectedDepartmentTab: null,
      disableAutoSelect: false,
      navTop: false,
      selectedItem: null,
      lookupItems: lookupList,
      showCouponModal: false,
      couponCode: '',
      fadeInClass: 'fade-in',
      errorModalVisible: false,
      errorMessage: '',
      isScrolling: false,
      scrollTimeout: null,
      programScrollId: 0,
      activeProgramScrollId: null,
      pendingDialogs: [], // Track dialogs that need to be shown
      dismissedBanners: [], // Track dismissed banner IDs
      showAgeVerification: false,
      ageVerificationResult: null,
      showQuantityPrompt: false,
      quantityPromptItem: null,
      quantityPromptDefault: 1,
    };
  },
  components: {
    LanguageSelector,
    ItemOrder,
    ItemOrderV2,
    SpaceX,
    LocationInfo,
    PillNavigation,
    Department,
    OrderControls,
    OrderSummary,
    QuestionAsker,
    GetTextComponent,
    BarcodeScanner,
    ErrorModal,
    AgeVerification,
    QuantityPrompt,
  },
  computed: {
    ...mapGetters(["screenOrientation"]),
    ...mapGetters("location", ["details", "config", "hasCoupons", "orderTypes"]),
    ...mapGetters("menu", ["departments", "items", "schedules", "availableItemRefs"]),
    ...mapGetters("customer", ["availableLastOrderedItems"]),
    ...mapGetters("terminal", ["pairingMethod"]),
    ...mapGetters("orderItem", ["item"]),
    ...mapGetters("order", {
      barcodeLookup: "barcodeLookup",
      type: "type",
      orderTypeId: "orderTypeId",
      orderItems: "items",
      subtotal: "subtotal",
      taxTotal: "taxTotal",
      grandTotal: "grandTotal",
      customerName: "name",
      barcodeActive: "barcodeActive",
      coupon: "coupon",
      total: "total",
      discounts: "discounts",
      surcharges: "surcharges",
    }),
    ...mapState("order", ["acceptedMessages"]),
    ...mapState(['setupMode']),
    isInSetupMode() {
      return this.setupMode.active && this.setupMode.screen === this.$route.name;
    },
    searchItems() {
      return this.items.filter(item => this.scheduledItemRefSet.has(item.ref));
    },
    itemsByRefMap() {
      return new Map(this.items?.map((item) => [item.ref, item]) || []);
    },
    scheduledItemRefSet() {
      // Use the same time-filtered + terminal-override-aware set that drives the menu itself
      return this.availableItemRefs || new Set();
    },
    hideQuantityControl() {

      if(this.config?.rails === 'olo') {
        return true;
      }

      if(this.config?.hideQuantityControl) {
        return true;
      }

      return false;
    },
    useItemOrderV2() {
      return this.config?.useWizard || false;
    },
    nextButtonText() {
      if (this.orderItems.length > 0 && this.grandTotal === 0) {
        return this.$t('submitOrderButtonText');
      } else {
        return this.orderType.view === 'restaurant' ? this.$t('checkout') : this.$t('pay');
      }
    },
    fullAddress() {
      return this.formatAddress(this.details);
    },
    orderType() {
      return this.config.orderTypes?.filter(ot => ot.active).find((ot) => ot.id === this.orderTypeId);
    },
    departmentTabs() {
      let tabs = this.departments?.map(department => ({
        ref: department.ref,
        name: department.name,
      })) || [];
      
      // Add tabs for each fast item group, sorted alphabetically
      this.fastItemGroupNamesSorted.forEach(groupName => {
        tabs.unshift({ // Add to the beginning
          ref: `fast-${groupName}`, // Use a unique ref prefix
          name: groupName
        });
      });
      
      return tabs;
    },
    groupedFastItems() {
      const configFastItems = this.config?.fastItems || [];
      
      if (!configFastItems || configFastItems.length === 0) {
        return {}; // Return empty object if no fast items
      }
      
      const availableFastItems = configFastItems.filter(fastItem => {
        // First, check if the item exists in the menu items
        const menuItem = this.itemsByRefMap.get(fastItem.itemRef);
        if (!menuItem) return false;
        
        // Then check if this item appears in any active department (based on current schedules)
        const isInActiveDepartment = this.scheduledItemRefSet.has(fastItem.itemRef);
        
        return isInActiveDepartment;
      });

      // Group by groupName (defaulting to 'Featured') and sort items within each group by order
      const groups = {};
      availableFastItems.forEach(item => {
        const group = item.groupName || 'Featured'; // Default group name
        if (!groups[group]) {
          groups[group] = [];
        }
        groups[group].push(item);
      });

      // Sort items within each group
      for (const group in groups) {
        groups[group].sort((a, b) => a.order - b.order);
      }

      return groups;
    },
    fastItemGroupNamesSorted() {
      return Object.keys(this.groupedFastItems).sort();
    },
    hasFastItems() {
      return Object.keys(this.groupedFastItems).length > 0 && this.orderType?.view === 'restaurant';
    },
    bannerMessages() {
      if (!this.orderType?.messages) return [];
      return this.orderType.messages
        .filter(m => m.type === 'banner' && !this.dismissedBanners.includes(m.id))
        .map(message => ({
          ...message,
          title: message.title,
          text: message.text
        }));
    },
  },
  mounted() {
    if (this.departmentTabs.length > 0) {
      this.selectedDepartmentTab = this.departmentTabs[0].ref;
    }
    
    this.$store.dispatch("orderItem/reset");
    this.$store.dispatch("order/clearPendingTicket");
    this.selectedItem = null;

    // Check for messages that need to be shown
    this.checkMessages();

    if (this.type && this.orderType.view === "restaurant") {
      this.scrollableDiv = this.$refs.menu;
      this.scheduleDepartmentsTopCheck();

      // Delay the scroll event binding to ensure the layout is stable
      this.$nextTick(() => {
        this.scrollableDiv.addEventListener("scroll", this.handleMenuScroll);
      });
    }

    if (this.barcodeLookup) {
      this.handleScan(this.barcodeLookup);
      this.$store.dispatch("order/setBarcodeLookup", null);
    }

    setTimeout(() => {
      this.fadeInClass = '';
    }, 300);
  },
  methods: {
    scheduleDepartmentsTopCheck() {
      if (this._depCheckRaf) return;
      this._depCheckRaf = requestAnimationFrame(() => {
        this._depCheckRaf = null;
        this.checkDepartmentsTop();
      });
    },
    formatAddress,
    transformFastItemMods(fastItemMods, fullItem) {
      if (!fastItemMods || !fullItem || !fullItem.modifierSets) {
        return [];
      }

      const detailedLineModifierSets = [];

      fastItemMods.forEach(fastModSet => {
        const menuModSet = fullItem.modifierSets.find(ms => ms.ref === fastModSet.modSetRef);
        if (menuModSet) {
          const lineModifiers = [];
          fastModSet.mods.forEach(modRef => {
            const menuMod = menuModSet.modifiers.find(m => m.ref === modRef);
            if (menuMod) {
              lineModifiers.push({
                modifierRef: menuMod.ref,
                name: menuMod.name,
                price: menuMod.price,
                quantity: 1, // Default quantity
                total: menuMod.price, // Initial total
                lineModifierSets: [], // Placeholder for potential nested mods
              });
            }
          });

          if (lineModifiers.length > 0) {
            detailedLineModifierSets.push({
              modifierSetRef: menuModSet.ref,
              name: menuModSet.name,
              lineModifiers: lineModifiers,
            });
          }
        }
      });

      return detailedLineModifierSets;
    },
    closeErrorModal() {
      this.errorModalVisible = false;
      this.errorMessage = '';
    },
    reset() {
      this.$router.push({ name: "start" });
    },
    setBarcodeActive(active) {
      this.$store.dispatch("order/setBarcodeActive", active);
    },
    seachSelected(item) {
      this.handleItemSelected(item, true);
    },
    menuSelected(item) {
      if (this.isScrolling) {
        return;
      }
      const targetRef = item.ref || item.itemRef;
      if (this.isCustomLabelQuantityEnabled(targetRef)) {
        const full = this.items.find(i => i.ref === targetRef) || item;
        this.openQuantityPromptForItem(full, 1);
        return;
      }
      this.handleItemSelected(item);
    },
    handleMenuScroll() {
      this.isScrolling = true;
      if (this.scrollTimeout) {
        clearTimeout(this.scrollTimeout);
      }
      this.scrollTimeout = setTimeout(() => {
        this.isScrolling = false;
      }, 500);

      this.scheduleDepartmentsTopCheck();
      const nav = this.$refs.nav.$el;

      if (!nav) return; // Ensure nav element exists

      const rect = nav.getBoundingClientRect(); // Ensure rect is defined after nav exists

      if (this.scrollableDiv.scrollTop > 0 && rect.top <= 1) {
        this.navTop = true;
      } else {
        this.navTop = false;
      }
    },
    isCustomLabelQuantityEnabled(itemRef) {
      const item = this.items.find(i => i.ref === itemRef);
      if (!item || !Array.isArray(item.customLabels)) return false;
      const configured = Array.isArray(this.config?.quantityQuickLabels)
        ? this.config.quantityQuickLabels
        : [];
      const labelsToMatch = configured.length > 0 ? configured : ['QUICK_QTY', 'QUANTITY_PROMPT'];
      const itemLabelsLower = item.customLabels.map(l => String(l || '').toLowerCase());
      const matchSet = new Set(labelsToMatch.map(l => String(l || '').toLowerCase()));
      return itemLabelsLower.some(l => matchSet.has(l));
    },
    openQuantityPromptForItem(fullItem, defaultQty = 1) {
      this.quantityPromptItem = fullItem;
      this.quantityPromptDefault = defaultQty;
      this.showQuantityPrompt = true;
    },
    onQuantityPromptConfirm(qty) {
      if (!this.quantityPromptItem) return;
      const base = this.quantityPromptItem;
      const image = base.images?.[0] || null;
      this.$store.dispatch("order/addItem", {
        ...base,
        itemRef: base.ref || base.itemRef,
        image,
        quantity: qty,
        total: base.price,
        priceLocked: base.priceLocked === true,
        lineModifierSets: base.lineModifierSets || [],
      }).then(() => {
        // Ensure quantity is correctly set on the newly added item (index 0)
        this.$store.dispatch("order/updateItemQuantity", { itemIndex: 0, quantity: qty });
      });
      this.quantityPromptItem = null;
      this.showQuantityPrompt = false;
    },
    checkDepartmentsTop() {
      if (this.disableAutoSelect) return;
      const parentTop = this.$refs.menu.getBoundingClientRect().top;
      const threshold = parentTop + 150;
      let best = null; // { ref, top }
      
      // First check Fast Item groups
      if (this.hasFastItems) {
        this.fastItemGroupNamesSorted.forEach(groupName => {
          const fastGroupElement = this.$el.querySelector(`#dep-fast-${groupName}`);
          if (fastGroupElement) {
            const fastGroupTop = fastGroupElement.getBoundingClientRect().top;
            if (fastGroupTop <= threshold && (!best || fastGroupTop > best.top)) {
              best = { ref: `fast-${groupName}`, top: fastGroupTop };
            }
          }
        });
      }
      
      // Then check regular departments
      this.departments.forEach((department) => {
        const sectionElement = this.$el.querySelector(`#dep-${department.ref}`);
        if (sectionElement) {
          const sectionTop = sectionElement.getBoundingClientRect().top;
          if (sectionTop <= threshold && (!best || sectionTop > best.top)) {
            best = { ref: department.ref, top: sectionTop };
          }
        }
      });

      if (best && this.selectedDepartmentTab !== best.ref) {
        this.selectedDepartmentTab = best.ref;
      }
    },
    async handleScan(barcode) {
      // Try weighted barcode first if enabled (UPC-A random weight: 2 + 5 PLU + 5 price + check)
      const weighted = this.config?.enableWeightedBarcodes ? this.parseWeightedBarcode(barcode) : null;
      if (weighted) {
        // Try to find matching item by PLU-like label
        const itemByPlu = this.items.find(i =>
          Array.isArray(i.customLabels) && i.customLabels.some(label => {
            const labelDigits = String(label || '').replace(/\D/g, '');
            return (
              labelDigits.startsWith('2' + weighted.plu) ||
              labelDigits.startsWith('02' + weighted.plu) ||
              labelDigits === weighted.plu ||
              labelDigits.endsWith(weighted.plu)
            );
          })
        );

        if (itemByPlu) {
          const pricedItem = { ...itemByPlu, price: weighted.price, priceLocked: true };
          this.handleItemSelected(pricedItem, true);
          return;
        }
        // Fallback to lookup list by PLU
        const lookupMatch = this.lookupItems.find(li =>
          Array.isArray(li.lookup) && (li.lookup.includes('2' + weighted.plu) || li.lookup.includes(weighted.plu))
        );
        if (lookupMatch?.ref) {
          const found = this.items.find(i => i.ref === lookupMatch.ref);
          if (found) {
            const pricedItem = { ...found, price: weighted.price, priceLocked: true };
            this.handleItemSelected(pricedItem, true);
            return;
          }
        }
        // If weighted pattern but no item found, continue with normal flow below
      }

      // first - assume barcodes are 12 digits - and sometimes have leading 0s
      // check that barcode is longer than 12 digits
      if (barcode.length >= 12) {
        // if so - then we need to check the last 12 digits
        const firstLookup = barcode.slice(-12);

        // then look for matches - also checking that item lookup is 12 digits and if not - then check the last 12 digits
        const foundItem = this.items.find(i => {
          const matched = i.customLabels.some(label => label.slice(-12) === firstLookup);
          return matched;
        });

        if (foundItem) {
          if (this.isCustomLabelQuantityEnabled(foundItem.ref || foundItem.itemRef)) {
            const full = this.items.find(i => i.ref === (foundItem.ref || foundItem.itemRef)) || foundItem;
            this.openQuantityPromptForItem(full, 1);
          } else {
            this.handleItemSelected(foundItem, true);
          }
          return;
        }
      }

      const normalizedBarcode = barcode.trim().toLowerCase();

      let item = this.items.find(item =>
        item.customLabels.some(label => label.toLowerCase().padStart(12, '0') === normalizedBarcode.padStart(12, '0'))
      );

      if (!item) {
        const lookupItems = this.lookupItems.filter(i =>
          i.lookup.includes(normalizedBarcode)
        );

        for (const lookupItem of lookupItems) {
          if (lookupItem.ref) {
            item = this.items.find(i => i.ref === lookupItem.ref);
            if (item) {
              break;
            }
          }
        }

        // if still no - then strip preceeding 0s and try again
        if (!item) {
          const noZeroBarcode = normalizedBarcode.replace(/^0+/, '');
          const lookupItemsNoZero = this.lookupItems.filter(i =>
            i.lookup.includes(noZeroBarcode)
          );

          for (const lookupItem of lookupItemsNoZero) {
            if (lookupItem.ref) {
              item = this.items.find(i => i.ref === lookupItem.ref);
              if (item) {
                break;
              }
            }
          }
        }
      }

      if (item) {
        this.handleItemSelected(item, true);
      } else {
        try {
          const response = await this.$store.dispatch("order/barcodeNotFound", normalizedBarcode);
          if (response.possibleMatch) {
            item = this.items.find(i => i.ref === response.possibleMatch.ref);
            if (item) {
              this.$store.dispatch("dialog/openDialog", {
                title: "Found Item",
                message: `Is this the right item? ${item.name}, Price: ${formatCurrency(item.price)}`,
                showCancel: true,
                onConfirm: () => this.handleItemSelected(item, true),
                onCancel: () => this.$store.dispatch("dialog/openDialog", {
                  title: "Item Not Found",
                  message: `Sorry, we couldn't find that item. Barcode: ${barcode}`
                })
              });
            }
          } else {
            this.$store.dispatch("dialog/openDialog", {
              title: "Item Not Found",
              message: "Sorry, we couldn't find that item."
            });
          }
        } catch (error) {
          this.$store.dispatch("dialog/openDialog", {
            title: "Error",
            message: "An error occurred during the barcode scan.",
          });
        } finally {
          this.$store.dispatch('doneLoading');
        }
      }
    },
    // Validate UPC-A using mod-10 algorithm
    isValidUpcA(code12) {
      if (!/^[0-9]{12}$/.test(code12)) return false;
      const digits = code12.split('').map(d => parseInt(d, 10));
      // Exclude the check digit (last)
      const data = digits.slice(0, 11);
      let oddSum = 0; // positions 1,3,5,7,9,11 (0-indexed even)
      let evenSum = 0; // positions 2,4,6,8,10 (0-indexed odd)
      for (let i = 0; i < data.length; i++) {
        if (i % 2 === 0) oddSum += data[i]; else evenSum += data[i];
      }
      const total = (oddSum * 3) + evenSum;
      const mod = total % 10;
      const check = mod === 0 ? 0 : 10 - mod;
      return check === digits[11];
    },
    parseWeightedBarcode(raw) {
      try {
        const digitsOnly = String(raw || '').replace(/\D/g, '');
        if (!digitsOnly || digitsOnly.length < 12) return null;
        const code12 = digitsOnly.slice(-12); // normalize to UPC-A layout
        // UPC-A random-weight number system '2'
        if (code12[0] !== '2') return null;
        if (!this.isValidUpcA(code12)) return null; // enforce check digit
        const plu = code12.slice(1, 6);
        const embedded = code12.slice(6, 11);
        let priceCents = parseInt(embedded, 10);
        // Handle common store schemes where the first of the 5 embedded digits is a flag
        // Example: 50624 -> $6.24 (drop leading '5')
        if (!Number.isNaN(priceCents)) {
          const firstEmbeddedDigit = embedded[0];
          if ((firstEmbeddedDigit >= '5' && firstEmbeddedDigit <= '9') || priceCents > 9999) {
            priceCents = parseInt(embedded.slice(1), 10);
          }
        }
        if (Number.isNaN(priceCents)) return null;
        return { plu, price: priceCents, upc: code12 };
      } catch (e) {
        return null;
      }
    },
    departmentTabSelected(ref) {
      this.selectedDepartmentTab = ref;
      this.disableAutoSelect = true;
      const scrollId = ++this.programScrollId;
      this.activeProgramScrollId = scrollId;

      // Standard UX: selecting the first tab resets the menu to the top.
      if (ref === this.departmentTabs?.[0]?.ref) {
        this.scrollToTopOfMenu(scrollId);
        return;
      }

      this.scrollToDepartment(ref, scrollId);
    },
    scrollToTopOfMenu(scrollId = null) {
      const menu = this.$refs.menu;
      if (menu) {
        this.navTop = false;
        this.smoothScroll(menu, 0, 500, () => {
          if (scrollId !== null && this.activeProgramScrollId === scrollId) {
            this.disableAutoSelect = false;
            this.activeProgramScrollId = null;
          }
        });
      }
    },
    departmentItems(department) {
      return department.items?.map((itemRef) => {
        const baseItem = this.itemsByRefMap.get(itemRef.ref);
        if (!baseItem) return null;
        const isReorder = Array.isArray(itemRef.lineModifierSets) && itemRef.lineModifierSets.length > 0;
        if (isReorder) {
          const filteredLineMods = this.filterAvailableModifiers(itemRef.lineModifierSets, baseItem.modifierSets);
          const removedModNames = this.getRemovedModNames(itemRef.lineModifierSets, filteredLineMods);
          return { ...baseItem, lineModifierSets: filteredLineMods, __fromReorder: true, __removedModNames: removedModNames };
        }
        return { ...baseItem, lineModifierSets: null };
      }).filter(Boolean);
    },
    filterAvailableModifiers(lineModifierSets, modifierSets) {
      if (!Array.isArray(lineModifierSets) || !Array.isArray(modifierSets)) return [];

      return lineModifierSets.reduce((result, lineModSet) => {
        const menuModSet = modifierSets.find(ms => ms.ref === lineModSet.modifierSetRef);
        if (!menuModSet) return result;

        const availableModRefs = new Set(menuModSet.modifiers.map(m => m.ref));
        const filteredLineModifiers = lineModSet.lineModifiers
          .filter(lm => availableModRefs.has(lm.modifierRef))
          .map(lm => {
            if (!Array.isArray(lm.lineModifierSets) || lm.lineModifierSets.length === 0) return lm;
            const menuMod = menuModSet.modifiers.find(m => m.ref === lm.modifierRef);
            if (!menuMod || !Array.isArray(menuMod.modifierSets)) return lm;
            return { ...lm, lineModifierSets: this.filterAvailableModifiers(lm.lineModifierSets, menuMod.modifierSets) };
          });

        if (filteredLineModifiers.length > 0) {
          result.push({ ...lineModSet, lineModifiers: filteredLineModifiers });
        }
        return result;
      }, []);
    },
    getRemovedModNames(original, filtered) {
      const collectMods = (lineModSets) => {
        const mods = [];
        (lineModSets || []).forEach(lms => {
          (lms.lineModifiers || []).forEach(lm => {
            mods.push({ ref: lm.modifierRef, name: lm.name });
            if (Array.isArray(lm.lineModifierSets)) {
              mods.push(...collectMods(lm.lineModifierSets));
            }
          });
        });
        return mods;
      };
      const originalMods = collectMods(original);
      const filteredRefs = new Set(collectMods(filtered).map(m => m.ref));
      return originalMods.filter(m => !filteredRefs.has(m.ref)).map(m => m.name);
    },
    handleItemSelected(item, bypassModal = false) {
      // Get the full item details
      let fullItem = null;

      // For fast items (from Featured department), find the original item and add fast item properties
      if (item.isFastItem === true) {
        fullItem = this.items.find((i) => i.ref === item.itemRef || i.ref === item.ref);

        if (!fullItem) {
          console.error("Fast Item base item not found", item);
          return;
        }

        // Transform the simplified mods into the detailed structure
        const detailedMods = this.transformFastItemMods(item.fastItemConfig.defaultMods, fullItem);

        if (!item.canBeModified) {
          // If fast item is not modifiable, add it directly to order with transformed mods
          this.orderItem({
            ...fullItem,
            lineModifierSets: detailedMods, // Use transformed mods
            instructions: item.fastItemConfig.customDescription || "" // Use custom description as instructions if available
          });
          return;
        }
        
        // If fast item is modifiable, start the order item process with transformed mods
        this.$store.dispatch("orderItem/start", {
          item: { ...fullItem, instructions: "" },
          quantity: 1,
          lineModifierSets: detailedMods, // Use transformed mods
        });
        return;
      }
      
      // Regular (non-fast) item handling
      if (item.departmentRef) {
        // Item is from a department (has departmentRef), find the full item
        fullItem = this.items.find((i) => i.ref === item.ref);
      } else {
        // Item is already a full item
        fullItem = item;
      }

      if (!fullItem) {
        console.error("Item not found", item);
        return;
      }

      // Determine if we should force the item modal to support special requests, even without modifiers.
      const shouldShowSpecialRequestModal =
        this.config?.rails === 'stm' &&
        this.orderType?.view === 'restaurant' &&
        this.config?.allowSpecialRequests === true;

      const hasModifierSets =
        Array.isArray(fullItem.modifierSets) && fullItem.modifierSets.length > 0;

      // Quantity lookup override for enabled departments
      const qtyCheckRef = fullItem.ref || fullItem.itemRef;
      if (this.isCustomLabelQuantityEnabled(qtyCheckRef)) {
        this.openQuantityPromptForItem(fullItem, 1);
        return;
      }
 
      // Check if modifiers are required and item is modifiable
      if (!bypassModal && (hasModifierSets || shouldShowSpecialRequestModal)) {
        // If selected from Order Again, prefill modifiers and suppress auto-opening nested UIs
        const prefilled = Array.isArray(fullItem.lineModifierSets) && fullItem.lineModifierSets.length > 0;
        const lineMods = prefilled ? fullItem.lineModifierSets : [];
        const suppressNestedAutoOpen = Boolean(fullItem.__fromReorder);
        const removedModNames = Array.isArray(fullItem.__removedModNames) ? fullItem.__removedModNames : [];
        this.$store.dispatch("orderItem/start", {
          item: { ...fullItem, instructions: "" },
          quantity: 1,
          lineModifierSets: lineMods,
          suppressNestedAutoOpen,
          removedModNames,
        });
      } else {
        // If bypassing modal and an explicit price was provided (e.g., weighted barcode), enforce it
        if (bypassModal && typeof item?.price === 'number') {
          fullItem = { ...fullItem, price: item.price, priceLocked: true };
        }
        this.orderItem(fullItem);
      }
    },
    handleItemEdit(payload) {
      const cartItem = payload.item;
      const index = payload.itemIndex;

      const item = this.items.find((item) => item.ref === cartItem.itemRef);
      if (item) {
        this.$store.dispatch("orderItem/start", {
          item: { ...item, instructions: cartItem.instructions },
          quantity: cartItem.quantity,
          lineModifierSets: cartItem.lineModifierSets,
          editIndex: index
        });
        this.selectedItem = item;
      }
    },
    handleRemoveCoupon() {
      this.$store.dispatch("order/removeCoupon");
    },
    close() {
      this.$store.dispatch("orderItem/close");
      this.selectedItem = null;
    },
    orderItem(item) {
      item.itemRef = item.ref || item.itemRef;
      const fullItem = this.items.find((i) => i.ref === item.itemRef);
      const image = fullItem.images?.[0] || null;
      this.$store.dispatch("order/addItem", {
        ...item,
        image,
        quantity: 1,
        total: item.price,
        priceLocked: item.priceLocked === true,
        lineModifierSets: item.lineModifierSets || [],
      });
      this.selectedItem = null;
    },
    async validateCouponCode(code) {
      const result = await this.$store.dispatch("coupon/validate", { code });
      return result;
    },
    handleApplyCoupon() {
      this.setBarcodeActive(true);
      this.showCouponModal = false;
    },
    showCouponModalHandler() {
      this.setBarcodeActive(false);
      this.showCouponModal = true;
    },
    removeItem(index) {
      this.$store.dispatch("order/removeItem", index);
    },
    orderTypeSelected(type) {
      if (type.orderDetails.filter(q => q.timing === 'startOrder').length > 0) {
        this.$router.push({ name: 'start', query: { orderTypeRef: type.id } });
      } else {
        this.$store.dispatch("order/setOrderType", type);
      }
    },
    scrollToDepartment(departmentRef, scrollId = null) {
      if (!this.$el.querySelector) return;

      const departmentElement = this.$el.querySelector(`#dep-${departmentRef}`);
      if (departmentElement) {
        const elementTop = departmentElement.getBoundingClientRect().top - this.$refs.menu.getBoundingClientRect().top + this.$refs.menu.scrollTop;

        const baseFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize);
        const desiredOffsetRems = 9;
        const desiredOffsetPixels = baseFontSize * desiredOffsetRems;

        const targetScrollTop = elementTop - desiredOffsetPixels;

        const menu = this.$refs.menu;
        const currentBottomPadding = parseFloat(getComputedStyle(menu).paddingBottom);
        const requiredHeight = targetScrollTop + menu.clientHeight;
        if (menu.scrollHeight - currentBottomPadding < requiredHeight) {
          const additionalPadding = requiredHeight - (menu.scrollHeight - currentBottomPadding);
          menu.style.paddingBottom = `${currentBottomPadding + additionalPadding}px`;
        }

        this.smoothScroll(menu, targetScrollTop, 600, () => {
          if (scrollId !== null && this.activeProgramScrollId === scrollId) {
            this.disableAutoSelect = false;
            this.activeProgramScrollId = null;
          }
        });
      }
    },
    smoothScroll(element, target, duration, onDone) {
      const start = element.scrollTop,
        change = target - start,
        startTime = performance.now();

      function scrollAnimation(currentTime) {
        const timeElapsed = currentTime - startTime;
        const progress = Math.min(1, timeElapsed / duration);
        const easeInOutQuad = t => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        element.scrollTop = start + change * easeInOutQuad(progress);
        if (timeElapsed < duration) {
          requestAnimationFrame(scrollAnimation);
        } else if (typeof onDone === 'function') {
          onDone();
        }
      }

      requestAnimationFrame(scrollAnimation);
    },
    handleQuestionCancel() {
      this.showQuestions = false;
    },
    async handleQuestionComplete(answers) {
      this.$store.dispatch("order/fillOrderDetails", answers);
      this.showQuestions = false;
      try {
        const result = await this.$store.dispatch("order/validate");

        if (result.success) {
          if (this.grandTotal > 0) {
            this.$router.push({ name: "checkout" });
          }
        } else {
          this.errorModalVisible = true;
          this.errorMessage = result.errorMessage;
        }
      } catch (e) {
        this.errorModalVisible = true;
        this.errorMessage = "An error occurred during the order validation.";
      }
    },
    prepareQuestionsToAsk(questions) {
      const propsToTranslate = new Set(['email', 'phone', 'badge', 'name']);
      return questions.map(q => {
        const prop = (q?.orderProperty || '').toString().toLowerCase();
        if (!propsToTranslate.has(prop)) return q;

        return {
          ...q,
          title: this.$t(`${prop}Question`),
          text: ''
        };
      });
    },
    async continueCheckout() {
      const orderDetailsClone = JSON.parse(JSON.stringify(this.orderType?.orderDetails || []));
      this.questionsToAsk = this.prepareQuestionsToAsk(orderDetailsClone.filter(q => q.timing === 'beforeValidate'));
      if (this.questionsToAsk.length > 0) {
        this.showQuestions = true;
        return;
      }

      try {
        const response = await this.$store.dispatch("order/validate");
        if (!response.success) {
          this.errorModalVisible = true;
          this.errorMessage = response.errorMessage;
          return;
        }
        if (this.grandTotal > 0) {
          this.$router.push({ name: "checkout" });
        }
      } catch (e) {
        this.errorModalVisible = true;
        this.errorMessage = "An error occurred during the order validation.";
        return;
      }
    },
    async handleCheckout() {
      // Check if terminal is linked by MID - if so, prevent checkout
      if (this.pairingMethod === 'mid') {
        this.$store.dispatch("dialog/openDialog", {
          title: "Checkout Not Available",
          message: "This terminal is in preview/demo mode. Checkout functionality is not available. This mode is intended for menu preview only.",
          showCancel: false,
          okText: 'OK',
          onConfirm: () => {
            // Just close the dialog - user stays on menu
          }
        });
        return;
      }

      // OLO rails + age verification check
      if (this.config?.rails === 'olo' && this.config?.ageVerification?.enabled) {
        const ageConfig = this.config.ageVerification;
        if (ageConfig.triggerByTag) {
          // Only trigger if any order item matches a tag in ageRequirements
          const tags = (ageConfig.ageRequirements || []).map(req => req.tag?.toUpperCase?.()).filter(Boolean);
          const hasTaggedItem = this.orderItems.some(orderItem => {
            // Find the full menu item for this order item
            const menuItem = this.items.find(i => i.ref === (orderItem.itemRef || orderItem.ref));
            if (!menuItem || !Array.isArray(menuItem.tags)) return false;
            return menuItem.tags.some(label => tags.includes(label.toUpperCase()));
          });
          if (!hasTaggedItem) {
            // No tagged item, skip age verification
            this.continueCheckout();
            return;
          }
        }
        // Otherwise, show age verification
        this.showAgeVerification = true;
        return;
      }

      if (this.orderType.promptCoupon && !this.coupon) {
        this.$store.dispatch("dialog/openDialog", {
          title: "Do you have a coupon code?",
          message: 'You have not entered a coupon code. Do you have one?',
          showCancel: true,
          okText: 'Yes',
          cancelText: 'No',
          onConfirm: () => {
            this.showCouponModal = true;
          },
          onCancel: () => {
            this.continueCheckout();
          }
        });
      } else {
        this.continueCheckout();
      }
    },
    onAgeVerificationComplete(result) {
      this.showAgeVerification = false;
      this.ageVerificationResult = result;
      // Now continue with the original checkout flow
      if (this.orderType.promptCoupon && !this.coupon) {
        this.$store.dispatch("dialog/openDialog", {
          title: "Do you have a coupon code?",
          message: 'You have not entered a coupon code. Do you have one?',
          showCancel: true,
          okText: 'Yes',
          cancelText: 'No',
          onConfirm: () => {
            this.showCouponModal = true;
          },
          onCancel: () => {
            this.continueCheckout();
          }
        });
      } else {
        this.continueCheckout();
      }
    },
    onAgeVerificationCancel() {
      this.showAgeVerification = false;
      this.ageVerificationResult = null;
      // User canceled age verification, stay on menu page
    },
    // Get items for a specific fast item group
    getFastItemsByGroup(groupName) {
      const groupItems = this.groupedFastItems[groupName];
      if (!groupItems) return [];
      
      return groupItems.map(fastItemConfig => {
        // Find the corresponding base menu item
        const baseItem = this.items.find(item => item.ref === fastItemConfig.itemRef);
        if (baseItem) {
          // Clone the base item to avoid modifying the original
          const enhancedItem = JSON.parse(JSON.stringify(baseItem));

          // Override properties for display and interaction using fastItemConfig
          enhancedItem.name = fastItemConfig.customName || baseItem.name;
          enhancedItem.description = fastItemConfig.customDescription || baseItem.description;
          
          // This 'ref' is used for item keying in the Department component. It MUST be unique.
          enhancedItem.ref = fastItemConfig.fastId;
 
          // This 'itemRef' is the reference to the original menu item in the main items list.
          enhancedItem.itemRef = fastItemConfig.itemRef; // Use the ref from fastItemConfig that links to the base menu item
 
          // Add other fast item specific properties
          enhancedItem.fastItemConfig = fastItemConfig; // Keep original config for reference, includes defaultMods, canBeModified, etc.
          enhancedItem.isFastItem = true;
          enhancedItem.canBeModified = typeof fastItemConfig.canBeModified === 'boolean' ? fastItemConfig.canBeModified : true;
          
          // Pass the configured default mods for potential display on the item card,
          // though transformation and application happen in handleItemSelected.
          enhancedItem.lineModifierSets = fastItemConfig.defaultMods || []; 
          
          // Calculate price including selected modifiers
          let calculatedPrice = baseItem.price; // Start with base price
          if (fastItemConfig.defaultMods && Array.isArray(fastItemConfig.defaultMods) && baseItem.modifierSets && Array.isArray(baseItem.modifierSets)) {
            fastItemConfig.defaultMods.forEach(selectedModSet => {
              const fullModSet = baseItem.modifierSets.find(ms => ms.ref === selectedModSet.modSetRef);
              if (fullModSet && fullModSet.modifiers && Array.isArray(selectedModSet.mods)) {
                selectedModSet.mods.forEach(selectedModRef => {
                  const modifierDetails = fullModSet.modifiers.find(m => m.ref === selectedModRef);
                  if (modifierDetails && typeof modifierDetails.price === 'number') {
                    calculatedPrice += modifierDetails.price;
                  }
                });
              }
            });
          }
          enhancedItem.price = calculatedPrice; // Set the final calculated price
          
          return enhancedItem;
        }
        return null;
      }).filter(Boolean);
    },
    checkMessages() {
      if (!this.orderType?.messages) return;
      const accepted = Array.isArray(this.acceptedMessages) ? this.acceptedMessages : [];
      
      // Get dialog messages that haven't been accepted yet
      const dialogMessages = this.orderType.messages.filter(m => 
        m.type === 'dialog' && 
        !accepted.some(am => am.id === m.id)
      );
      
      // Store pending dialogs and show the first one
      this.pendingDialogs = [...dialogMessages];
      if (this.pendingDialogs.length > 0) {
        this.showNextDialog();
      }
    },
    
    showNextDialog() {
      // If no dialogs or we're done, exit
      if (!this.pendingDialogs || this.pendingDialogs.length === 0) return;
      
      // Get the first dialog in the queue
      const message = this.pendingDialogs[0];
      
      // Remove current dialog from pending list
      this.pendingDialogs.shift();
      
      this.$store.dispatch("dialog/openDialog", {
        title: message.title,
        message: message.text,
        showCancel: message.showCancel,
        okText: message.buttonText || 'OK',
        cancelText: message.cancelText || 'Cancel',
        onConfirm: () => {
          this.$store.dispatch("order/acceptMessage", message);
          // Wait for dialog animation to complete before showing next
          setTimeout(() => {
            this.showNextDialog();
          }, 300);
        },
        onCancel: () => {
          // Wait for dialog animation to complete before showing next
          setTimeout(() => {
            this.showNextDialog();
          }, 300);
        }
      });
    },
    handleQuantityPromptConfirm(quantity) {
      if (this.quantityPromptItem) {
        this.orderItem({
          ...this.quantityPromptItem,
          quantity: quantity,
          total: this.quantityPromptItem.price * quantity,
        });
        this.showQuantityPrompt = false;
        this.quantityPromptItem = null;
        this.quantityPromptDefault = 1;
      }
    },
  },
  watch: {
    item(newItem) {
      if (newItem) {
        this.setBarcodeActive(false);
      } else {
        this.setBarcodeActive(true);
      }
    },
    type(newType) {
      if (newType && this.orderType.view === "restaurant") {
        this.$nextTick(() => {
          this.scrollableDiv = this.$refs.menu;
          this.scrollableDiv.addEventListener("scroll", this.handleMenuScroll);
          this.scheduleDepartmentsTopCheck();
        });
      } else {
        this.scrollableDiv = this.$refs.menu;
        if (!this.scrollableDiv) return;

        this.scrollableDiv.removeEventListener("scroll", this.handleMenuScroll);
      }
    },
  },
  beforeUnmount() {
    if (this.scrollableDiv) {
      this.scrollableDiv.removeEventListener("scroll", this.handleMenuScroll);
    }
    if (this._depCheckRaf) {
      cancelAnimationFrame(this._depCheckRaf);
      this._depCheckRaf = null;
    }
  },
};
</script>

<style scoped>
.menu-col .search-button-hold {
  position: fixed;
  top: 1.875rem;
  right: 44.25rem;
}

.banner-messages {
  padding: 0 0 1rem 0;
  margin-bottom: 1rem;
  margin-left: 2.5rem;
  margin-right: 2.5rem;
}

.banner-messages .banner-alert {
  margin-bottom: 0.75rem;
  box-shadow: 0 2px 6px rgba(0,0,0,0.08);
  border-radius: 0.5rem;
  padding: 1.25rem 1.5rem;
  border: none;
  display: flex;
  align-items: flex-start;
  position: relative;
  transition: all 0.2s ease;
  background-color: #ffffff;
}

.banner-messages .banner-alert:last-child {
  margin-bottom: 0;
}

.banner-icon {
  margin-right: 1rem;
  margin-top: 0.25rem;
  font-size: 1.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.alert-info .banner-icon {
  color: #222222;
}

.alert-success .banner-icon {
  color: #198754;
}

.alert-warning .banner-icon, .alert-danger .banner-icon {
  color: #dc3545;
}

.banner-messages .alert-info {
  background-color: #ffffff;
  border-left: 4px solid #222222;
}

.banner-messages .alert-success {
  background-color: #ffffff;
  border-left: 4px solid #198754;
}

.banner-messages .alert-warning, .banner-messages .alert-danger {
  background-color: #ffffff;
  border-left: 4px solid #dc3545;
}

.banner-messages .alert-heading {
  font-size: 1.5rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  letter-spacing: -0.01em;
  color: #111111;
}

.banner-content {
  flex-grow: 1;
}

.banner-content p {
  font-size: 1.125rem;
  line-height: 1.4;
  letter-spacing: 0;
  color: #222222;
  margin-top: 0;
}

.banner-close {
  background: none;
  border: none;
  font-size: 1.5rem;
  color: rgba(0, 0, 0, 0.5);
  margin-left: 1rem;
  cursor: pointer;
  transition: color 0.15s;
  line-height: 1;
  padding: 0.5rem;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  margin-top: -0.25rem;
}

.banner-close:hover {
  color: rgba(0, 0, 0, 0.8);
  background-color: rgba(0, 0, 0, 0.05);
}

.banner-fade-enter-active, .banner-fade-leave-active {
  transition: opacity 0.25s, transform 0.25s;
}
.banner-fade-enter-from, .banner-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.top-right {
  position: absolute;
  top: 2.5rem;
  right: 45rem;
}

.row>* {
  padding: 0;
}

.layout {
  display: flex;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
}

.menu-col {
  margin: 0;
  padding: 0;
  overflow-y: scroll;
  overflow-x: hidden;
  height: 100vh;
  flex: 1;
}

.menu-col .departments {
  padding: 0 2.5rem 3rem 2.5rem;
}

.order-col {
  width: 43rem !important;
  overflow-y: scroll;
  height: 100vh;
  border-left: 0.125rem solid #eaeaea;
  padding: 0;
  background-color: var(--primary-white);
}

/* Vertical */

.vertical .layout {
  flex-direction: column;
}

.vertical .menu-col,
.vertical .order-col {
  width: 100% !important;
}

.vertical .menu-col {
  height: calc(100vh - 32rem);
}

.vertical .order-col {
  height: 32rem;
  border-top: 0.125rem solid var(--grey-outline);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.vertical .order-col .controls {
  flex-shrink: 0;
}
</style>
